import moment from 'moment'
import type { FollowUpCell } from '@/utils/followUps'

export function standardFormatter(value: string): string {
  return value ? value : '-'
}
const ACCOUNT_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active',
  BANKRUPT: 'Bankrupt',
  DO_NOT_CONTACT: 'Do Not Contact',
  NO_COMMUNICATION: 'No Communication',
  VOIDED: 'Voided',
  REPO: 'Repo',
  INACTIVE: 'Inactive'
}
export function accountStatusFormatter(status: string): string {
  if (!status) return '-'

  return ACCOUNT_STATUS_LABELS[status] || status
}
// Payment Outcome on Call Logs. "paid" (the customer paid on the call) and
// "future_payment" (the customer committed to pay later); unknown / no outcome renders blank.
const PAYMENT_OUTCOME_LABELS: Record<string, string> = {
  paid: 'Paid',
  future_payment: 'Future Payment'
}
export function paymentOutcomeFormatter(
  value: string | null | undefined
): string {
  if (!value) return ''

  return PAYMENT_OUTCOME_LABELS[value] || ''
}
// One vocabulary for every status the Payment Report prints, whichever table it came out of. Three
// sources feed that one column: the payment's own PaymentStatus, the processor's Transaction.Status,
// and InComm's lowercase set.
const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PAID: 'Paid',
  FAILED: 'Failed',
  REFUND: 'Refund',
  PARTIALLY_REFUNDED: 'Partially Refunded',
  ACH_RETURN: 'ACH Return',
  PENDING: 'Pending',
  NEEDS_REVIEW: 'Needs Review',
  CANCELLED: 'Cancelled',
  VOID: 'Void',
  CHARGEBACK: 'Chargeback',
  OK: 'Paid',
  REJECTED: 'ACH Return',
  DECLINED: 'Declined',
  PROMISED: 'Promised',
  PROMISE_CONFIRMED: 'Promise Confirmed',
  VERIFY_OK: 'Verify Ok',
  VERIFY_DECLINED: 'Verify Declined',
  ERROR: 'Error'
}

// Tender types and card brands. ACH stays an acronym and Apple Pay keeps its
// space; everything else is a word and gets titled.
const PAYMENT_TYPE_LABELS: Record<string, string> = {
  ACH: 'ACH',
  APPLEPAY: 'Apple Pay',
  VISA: 'Visa',
  VISAELECTRON: 'Visa Electron',
  MASTERCARD: 'Mastercard',
  AMEX: 'Amex',
  DISCOVER: 'Discover',
  CASH: 'Cash',
  CHECK: 'Check',
  MONEY_ORDER: 'Money Order',
  CREDIT: 'Credit',
  DEBIT: 'Debit'
}

// An unmapped value still gets cased, so a status added to the API before it is
// added here reads as a label rather than as a database constant.
function titleCase(value: string): string {
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, character => character.toUpperCase())
}

function labelled(
  labels: Record<string, string>,
  value: string | null | undefined
): string {
  if (!value) return '-'

  const key = String(value).toUpperCase().replace(/ /g, '_')

  return labels[key] || titleCase(String(value))
}

export function paymentStatusLabel(value: string | null | undefined): string {
  return labelled(PAYMENT_STATUS_LABELS, value)
}

export function paymentTypeLabel(value: string | null | undefined): string {
  return labelled(PAYMENT_TYPE_LABELS, value)
}

// A refund row carries a Transaction.Status too, but the same value means something different on money
// going back out: OK is a completed refund, not a payment received, and REJECTED is a refused refund
// rather than an ACH return. So it gets its own map instead of borrowing the payment one.
const REFUND_STATUS_LABELS: Record<string, string> = {
  OK: 'Complete',
  PENDING: 'Pending',
  DECLINED: 'Declined',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
  VOID: 'Void',
  REFUND: 'Refund',
  ERROR: 'Error'
}

export function refundStatusLabel(value: string | null | undefined): string {
  return labelled(REFUND_STATUS_LABELS, value)
}

export function basisPointsFormatter(amount: string): string {
  return (Number(amount) / 100).toString() + '%'
}
export function moneyFormatter(amount: string | number): string {
  if (amount) {
    const parsedAmount =
      typeof amount === 'string' ? parseFloat(amount) : amount
    const isNegative = parsedAmount < 0
    let formattedAmount = Math.abs(parsedAmount).toFixed(2).toString()

    if (formattedAmount.split('.').length === 1) {
      formattedAmount = formattedAmount + '.00'
    }

    const formatted =
      '$' + formattedAmount.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    // Return with parentheses for negative values
    return isNegative ? `(${formatted})` : formatted
  } else {
    return '$0.00'
  }
}
export function numberFormatter(number: string): string {
  if (number) {
    number = number.toString()
    if (number.split('.')[1]?.length === 1) {
      number = number + '0'
    }

    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  } else {
    return '0'
  }
}
export function dateFormatter(date: string): string {
  return date ? moment(date, 'YYYY/MM/DD').format('MM/DD/YYYY') : '-'
}
export function dateTimeLocalFormatter(date: string): string {
  return date ? moment(date).local().format('L LT') : '-'
}
export function percentageFormatter(percentage: string): string {
  return percentage
    ? percentage.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '%'
    : '-'
}
export function phoneFormatter(phoneNum: string): string {
  if (phoneNum) {
    phoneNum = ('' + phoneNum).replace(/\D/g, '')
    const match = phoneNum.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)
    if (match) {
      const intlNum = match[1] ? '+1 ' : ''

      return [intlNum, '(', match[2], ') ', match[3], '-', match[4]].join('')
    }

    return phoneNum
  } else {
    return '-'
  }
}
export function promiseFormatter(promise: string): string {
  if (promise === 'PROMISE_CONFIRMED' || promise === 'SCHEDULED') {
    return 'Confirmed'
  } else if (promise === 'PROMISED') {
    return 'Unconfirmed'
  } else {
    return '-'
  }
}

export function promiseDateFormatter(
  date: string | null,
  status: string | null
): string {
  const formattedDate = date ? moment(date).local().format('L') : null
  if (!formattedDate) {
    return '-'
  }
  if (status === 'PROMISE_CONFIRMED' || status === 'SCHEDULED') {
    return `${formattedDate} (C)`
  }
  if (status === 'PROMISED') {
    return `${formattedDate} (U)`
  }

  return formattedDate
}

export function lastWorkedFormatter(lastWorked: string): string {
  if (!lastWorked) {
    return '-'
  }

  return moment(lastWorked).format('YYYY-MM-DD')
}

/**
 * The customer list's Follow-up cell: the scheduled follow-up's date, blank when there is none.
 * Unlike {@link lastWorkedFormatter} this renders blank rather than '-' per the column's design.
 *
 * @param followUp - the row's `follow_up` cell, or null
 * @returns YYYY-MM-DD, or '' when there is no scheduled follow-up
 */
export function followUpFormatter(followUp: FollowUpCell | null): string {
  if (!followUp) return ''

  return moment(followUp.date).format('YYYY-MM-DD')
}

export function smartInboxDateFormatter(date: string): string {
  if (!date) return '-'

  const messageDate = moment(date)
  const now = moment()
  const daysDiff = now.diff(messageDate, 'days')

  if (daysDiff < 7) {
    return messageDate.format('ddd h:mm A')
  }

  return messageDate.format('M/D h:mm A')
}
